import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FlexAlignCenter, FlexEnd, WrappingText } from 'components/Elements';
import { sizes, theme } from 'theme';
import { Activity } from 'models/Activity';
import Table, { TableCell } from 'components/table/Table';
import useWindowWidth from 'hooks/useWindowWidth';
import strings from 'strings/strings.json';
import DownloadIcon from 'assets/icons/controls/DownloadIcon';
import { API_URL } from 'lib/_api-helpers';
import Button from '../../../../components/buttons/Button';
import QuestionnaireIcon from '../../../../assets/icons/pathway/QuestionnaireIcon';
import { IconSize } from '../../../../assets/icons/icon-sizes';
import { downloadCsvFile } from '../../../../lib/downloadCsvFile';
import { fetchAuthSession } from 'aws-amplify/auth';
import useGetRequest from 'hooks/useGetRequest';
import { ActivityCompletionCount } from 'models/ActivityCompletionCount';
import { logError } from 'lib/debug-helpers';

type QuestionnaireResultsProps = {
    questionnaires: Activity[];
};

type DownloadError = {
    activityId: string;
    error: string;
};

export default function QuestionnaireResults({ questionnaires }: QuestionnaireResultsProps): JSX.Element {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { isDesktopWidth } = useWindowWidth();

    const [downloadError, setDownloadError] = React.useState<DownloadError | null>(null);

    const activityIds = questionnaires.map((questionnaire) => questionnaire.id).filter(Boolean);
    const activityIdsParam = activityIds.join(',');
    const delayCounts = !courseId || activityIds.length === 0;

    const { data: responseCounts, loading: loadingCounts, errors: countErrors } =
        useGetRequest<ActivityCompletionCount[]>(
            `activities/completion-counts?course_schedule_id=${courseId ?? ''}&activity_ids=${activityIdsParam}`,
            delayCounts,
        );

    React.useEffect(() => {
        if (countErrors.length > 0) {
            logError(countErrors);
        }
    }, [countErrors]);

    const responseCountMap = React.useMemo(() => {
        return new Map((responseCounts ?? []).map((count) => [count.activityId, count.completedCount]));
    }, [responseCounts]);

    if (!questionnaires) return <></>;

    async function downloadFile(activityId: string): Promise<void> {
        setDownloadError(null);

        const activity = questionnaires.find((questionnaire) => questionnaire.id === activityId);

        if (!activity) {
            return;
        }

        const url = `${API_URL}/activities/answers/file?activity_id=${activity.id}&course_schedule_id=${courseId}`;

        let token: string | undefined;

        try {
            const session = await fetchAuthSession();
            token = session.tokens?.accessToken?.toString();
            if (!token) {
                navigate('/signin');
                return;
            }
        } catch (error) {
            navigate('/signin');
            return;
        }

        const downloadResponse = await downloadCsvFile(url, token, activity.title);

        if (downloadResponse.status === 204) {
            setDownloadError({ activityId: activity.id, error: 'There are no responses yet' });
            return;
        }

        if (!downloadResponse.ok) {
            setDownloadError({ activityId: activity.id, error: 'An unknown error occurred' });
            return;
        }
    }

    return (
        <Table
            headers={[
                {
                    name: strings.scheduledQuestionnaire.questionnaireTitle,
                    rowPercentage: 50,
                },
                {
                    name: strings.scheduledQuestionnaire.responsesTitle,
                    rowPercentage: 30,
                },
                {
                    name: '',
                    rowPercentage: 20,
                    mobileRightColumn: true,
                },
            ]}
            rows={questionnaires.map((activity) => {
                const completedCount = responseCountMap.get(activity.id) ?? 0;

                const showError = !!downloadError && downloadError.activityId === activity.id && !!downloadError.error;

                const cells: TableCell[] = [
                    {
                        name: strings.scheduledQuestionnaire.questionnaireTitle,
                        value: (
                            <FlexAlignCenter>
                                <IconContainer>
                                    <QuestionnaireIcon size={IconSize.MEDIUM} />
                                </IconContainer>
                                <WrappingText>{activity.title}</WrappingText>
                            </FlexAlignCenter>
                        ),
                    },
                    {
                        name: strings.scheduledQuestionnaire.responsesTitle,
                        value: isDesktopWidth ? (
                            <FlexAlignCenter>
                                <WrappingText>{completedCount}</WrappingText>
                            </FlexAlignCenter>
                        ) : (
                            <FlexAlignCenter>
                                <WrappingText>
                                    {strings.scheduledQuestionnaire.responsesTitle}: {completedCount}
                                </WrappingText>
                            </FlexAlignCenter>
                        ),
                    },
                    {
                        name: '',
                        value: showError ? (
                            <div>{downloadError.error}</div>
                        ) : (
                            <FlexEnd>
                                <Button
                                    title={strings.scheduledQuestionnaire.downloadQuestionnaireButton}
                                    aria={strings.scheduledQuestionnaire.downloadQuestionnaireButtonAria}
                                    icon={<DownloadIcon />}
                                    disabled={loadingCounts || completedCount === 0}
                                    onClick={async () => {
                                        await downloadFile(activity.id);
                                    }}
                                />
                            </FlexEnd>
                        ),
                    },
                ];

                return {
                    id: activity.id,
                    cells: cells,
                };
            })}
        />
    );
}

const IconContainer = styled(FlexAlignCenter)`
    background-color: ${theme.iconBackgroundColour};
    border-radius: ${sizes.borderRadiusMd};
    margin-right: ${sizes.spacingSm};
    padding: ${sizes.spacingXs};
`;
